import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, movies, getMoviesByCategory, getCategoryName } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

type SortKey = 'rating' | 'year' | 'views';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<SortKey>('rating');
  const categoryName = slug ? getCategoryName(slug) : '全部';

  const filteredMovies = useMemo(() => {
    let list = slug ? getMoviesByCategory(slug) : movies;
    return [...list].sort((a, b) => b[sortBy] - a[sortBy]);
  }, [slug, sortBy]);

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: 'rating', label: '评分最高' },
    { key: 'year', label: '最新发布' },
    { key: 'views', label: '最受欢迎' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-8 md:py-10">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryName },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {categoryName}
            </h1>
            <p className="text-foreground-500 text-sm">
              共找到 <strong className="text-primary-500">{filteredMovies.length}</strong> 部影片
            </p>

            <div className="flex flex-wrap gap-2 mt-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full bg-background-50 py-6">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-2 mb-6">
              <span className="text-foreground-500 text-xs whitespace-nowrap">排序：</span>
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-5 stagger-children">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                  <i className="ri-movie-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无影片</p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}